import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { authSelector } from '../../features/auth/slice';
import axios from 'axios';
import { Activity, Eye, Calendar, User, ChevronLeft, ChevronRight, Trash2, Filter, RefreshCw } from 'lucide-react';

const USER_TYPE_LABELS = {
  member: { label: '일반회원', className: 'bg-sky-500/20 text-sky-300 border border-sky-500/30' },
  client: { label: '권리사', className: 'bg-amber-500/20 text-amber-300 border border-amber-500/30' },
};

function formatTime(dateStr) {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function formatDateTime(dateStr) {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return d.toLocaleDateString('ko-KR', { month: '2-digit', day: '2-digit' }) + ' ' + formatTime(dateStr);
}

export default function AdminAccessLogPage() {
  const { me } = useSelector(authSelector.selectAuthState);
  const navigate = useNavigate();
  useEffect(() => {
    if (me && me.superAdminYn !== 'Y') navigate(-1);
  }, [me, navigate]);

  const [view, setView] = useState('summary');
  const [date, setDate] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  });
  const [summary, setSummary] = useState(null);
  const [summaryPage, setSummaryPage] = useState(1);
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filterType, setFilterType] = useState('');
  const SUMMARY_PAGE_SIZE = 30;
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const deleteByDate = async () => {
    if (!window.confirm(`${date} 접속 로그를 전체 삭제하시겠습니까?`)) return;
    try {
      const res = await axios.delete('/api/access-log/admin', { params: { date } });
      alert(res.data.message);
      setRefreshKey(k => k + 1);
    } catch { alert('삭제 실패'); }
  };

  const deleteSingle = async (id) => {
    try {
      await axios.delete(`/api/access-log/admin/${id}`);
      setLogs(prev => prev.filter(l => l.id !== id));
      setTotal(t => t - 1);
    } catch { alert('삭제 실패'); }
  };

  const deleteByUser = async (loginId, userName) => {
    if (!window.confirm(`${userName}의 ${date} 접속 로그를 삭제하시겠습니까?`)) return;
    try {
      const res = await axios.delete('/api/access-log/admin', { params: { date, loginId } });
      alert(res.data.message);
      setRefreshKey(k => k + 1);
    } catch { alert('삭제 실패'); }
  };

  const fetchSummary = useCallback(() => {
    setLoading(true);
    axios.get('/api/access-log/admin/summary', { params: { date } })
      .then(res => setSummary(res.data))
      .catch(() => setSummary(null))
      .finally(() => setLoading(false));
  }, [date]);

  const fetchDetail = useCallback(() => {
    setLoading(true);
    const params = { page, limit: 50, date };
    if (filterType) params.userType = filterType;
    axios.get('/api/access-log/admin', { params })
      .then(res => { setLogs(res.data.logs); setTotal(res.data.total); })
      .catch(() => { setLogs([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, [date, page, filterType]);

  useEffect(() => {
    if (view === 'summary') fetchSummary();
    else fetchDetail();
  }, [view, fetchSummary, fetchDetail, refreshKey]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => {
      if (view === 'summary') fetchSummary();
      else fetchDetail();
    }, 5000);
    return () => clearInterval(interval);
  }, [autoRefresh, view, fetchSummary, fetchDetail]);

  const changeDate = (delta) => {
    const d = new Date(date);
    d.setDate(d.getDate() + delta);
    setDate(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
    setPage(1);
    setSummaryPage(1);
  };

  return (
    <div>
      {/* 헤더 */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Activity size={20} className="text-amber-400" />
          <h1 className="text-lg font-bold text-white">접속기록</h1>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => setAutoRefresh(v => !v)}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              autoRefresh ? 'bg-emerald-500/20 text-emerald-400' : 'bg-white/5 text-slate-400'
            }`}
          >
            <RefreshCw size={11} className={autoRefresh ? 'animate-spin' : ''} />
            {autoRefresh ? '자동갱신' : '수동'}
          </button>
          <button
            onClick={() => setRefreshKey(k => k + 1)}
            className="px-3 py-1.5 rounded-lg text-xs bg-white/5 text-slate-400 hover:text-white transition-colors"
          >새로고침</button>
          <button
            onClick={() => setView('summary')}
            className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              view === 'summary' ? 'bg-amber-500/20 text-amber-300' : 'bg-white/5 text-slate-400 hover:text-white'
            }`}
          >요약</button>
          <button
            onClick={() => setView('detail')}
            className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              view === 'detail' ? 'bg-amber-500/20 text-amber-300' : 'bg-white/5 text-slate-400 hover:text-white'
            }`}
          >상세</button>
        </div>
      </div>

      {/* 날짜 선택기 */}
      <div className="flex items-center gap-2 mb-6">
        <button onClick={() => changeDate(-1)} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-slate-400 hover:text-white transition-colors">
          <ChevronLeft size={16} />
        </button>
        <div className="flex items-center gap-2 bg-white/5 rounded-lg px-3 py-2">
          <Calendar size={14} className="text-slate-400" />
          <input
            type="date"
            value={date}
            onChange={e => { setDate(e.target.value); setPage(1); }}
            className="bg-transparent text-sm text-white outline-none"
          />
        </div>
        <button onClick={() => changeDate(1)} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-slate-400 hover:text-white transition-colors">
          <ChevronRight size={16} />
        </button>
        <button
          onClick={() => {
            const now = new Date();
            setDate(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`);
            setPage(1);
          }}
          className="px-3 py-2 rounded-lg text-xs bg-white/5 text-slate-400 hover:text-white transition-colors"
        >오늘</button>
        <div className="flex-1" />
        <button
          onClick={deleteByDate}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs text-red-400 hover:bg-red-500/10 transition-colors"
        >
          <Trash2 size={13} /> 이 날짜 전체 삭제
        </button>
      </div>

      {loading && <div className="text-center py-12 text-slate-500">로딩 중...</div>}

      {/* 요약 뷰 */}
      {!loading && view === 'summary' && summary && (() => {
        const allUsers = summary.users;
        const totalPages = Math.ceil(allUsers.length / SUMMARY_PAGE_SIZE);
        const pagedUsers = allUsers.slice((summaryPage - 1) * SUMMARY_PAGE_SIZE, summaryPage * SUMMARY_PAGE_SIZE);
        return (
          <div>
            {allUsers.length === 0 ? (
              <div className="text-center py-16 text-slate-500">
                <User size={40} className="mx-auto mb-3 opacity-30" />
                <p>이 날짜에 접속 기록이 없습니다.</p>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs text-slate-500">총 {allUsers.length}명</span>
                  {totalPages > 1 && (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setSummaryPage(p => Math.max(1, p - 1))}
                        disabled={summaryPage === 1}
                        className="p-1 rounded bg-white/5 text-slate-400 disabled:opacity-30 hover:text-white transition-colors"
                      ><ChevronLeft size={14} /></button>
                      <span className="text-xs text-slate-500">{summaryPage} / {totalPages}</span>
                      <button
                        onClick={() => setSummaryPage(p => Math.min(totalPages, p + 1))}
                        disabled={summaryPage === totalPages}
                        className="p-1 rounded bg-white/5 text-slate-400 disabled:opacity-30 hover:text-white transition-colors"
                      ><ChevronRight size={14} /></button>
                    </div>
                  )}
                </div>
                <div className="space-y-3">
                  {pagedUsers.map((u, i) => {
                    const typeInfo = USER_TYPE_LABELS[u.userType] || { label: u.userType, className: 'bg-white/10 text-slate-400 border border-white/10' };
                    return (
                      <div key={i} className="bg-white/[0.03] rounded-xl border border-white/[0.06] p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-white/5 flex items-center justify-center">
                              <User size={16} className="text-slate-400" />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-white text-sm">{u.userName}</span>
                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${typeInfo.className}`}>{typeInfo.label}</span>
                              </div>
                              <p className="text-xs text-slate-500">{u.loginId}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-500">{u.pages.length}개 페이지</span>
                            <button
                              onClick={() => deleteByUser(u.loginId, u.userName)}
                              className="p-1.5 rounded-lg hover:bg-red-500/10 text-slate-500 hover:text-red-400 transition-colors"
                              title="이 사용자의 로그 삭제"
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </div>
                        {u.pages.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {u.pages.map((p, j) => (
                              <span
                                key={j}
                                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs bg-blue-500/10 text-blue-300 border border-blue-500/20"
                              >
                                <Eye size={11} />
                                <span className="font-mono">{p.path}</span>
                                <span className="text-blue-500 text-[10px]">{formatTime(p.at)}</span>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        );
      })()}

      {/* 상세 뷰 */}
      {!loading && view === 'detail' && (
        <>
          <div className="flex items-center gap-3 mb-4">
            <Filter size={14} className="text-slate-400" />
            <div className="flex items-center gap-1.5">
              {[['', '전체'], ['member', '일반회원'], ['client', '권리사']].map(([val, label]) => (
                <button
                  key={val}
                  onClick={() => { setFilterType(val); setPage(1); }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    filterType === val ? 'bg-amber-500/20 text-amber-300' : 'bg-white/5 text-slate-400 hover:text-white'
                  }`}
                >{label}</button>
              ))}
            </div>
            <span className="ml-auto text-xs text-slate-500">{total}건</span>
          </div>

          <div className="bg-white/[0.03] rounded-xl border border-white/[0.06] overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/[0.06] text-xs text-slate-500">
                  <th className="text-left px-4 py-3 font-medium">시간</th>
                  <th className="text-left px-4 py-3 font-medium">이름</th>
                  <th className="text-left px-4 py-3 font-medium">구분</th>
                  <th className="text-left px-4 py-3 font-medium">페이지</th>
                  <th className="px-4 py-3 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 ? (
                  <tr><td colSpan={5} className="text-center py-12 text-slate-500">접속 기록이 없습니다</td></tr>
                ) : (
                  logs.map(log => {
                    const typeInfo = USER_TYPE_LABELS[log.userType] || { label: log.userType, className: 'bg-white/10 text-slate-400 border border-white/10' };
                    return (
                      <tr key={log.id} className="border-b border-white/[0.04] hover:bg-white/[0.02] transition-colors">
                        <td className="px-4 py-2.5 text-slate-400 text-xs font-mono whitespace-nowrap">{formatDateTime(log.createdAt)}</td>
                        <td className="px-4 py-2.5">
                          <div className="font-medium text-white text-xs">{log.userName}</div>
                          <div className="text-slate-500 text-[11px]">{log.loginId}</div>
                        </td>
                        <td className="px-4 py-2.5">
                          <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium border ${typeInfo.className}`}>{typeInfo.label}</span>
                        </td>
                        <td className="px-4 py-2.5 text-slate-300 text-xs font-mono">{log.path}</td>
                        <td className="px-4 py-2.5 text-center">
                          <button onClick={() => deleteSingle(log.id)} className="p-1 rounded hover:bg-red-500/10 text-slate-500 hover:text-red-400 transition-colors">
                            <Trash2 size={12} />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {total > 50 && (
            <div className="flex items-center justify-center gap-2 mt-4">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1.5 rounded-lg bg-white/5 text-slate-400 disabled:opacity-30 text-xs hover:text-white transition-colors"
              >이전</button>
              <span className="text-xs text-slate-500">{page} / {Math.ceil(total / 50)}</span>
              <button
                onClick={() => setPage(p => p + 1)}
                disabled={page >= Math.ceil(total / 50)}
                className="px-3 py-1.5 rounded-lg bg-white/5 text-slate-400 disabled:opacity-30 text-xs hover:text-white transition-colors"
              >다음</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
